import { useState, useRef, useCallback, useEffect } from "react";
import _ from "lodash";
import { ElementComponentData } from "data/form/elementSelectors";

type UpdateProps = { id: string; value: string | number };

const useDebounceSetDataReactive = (
	callback: (
		currentData: ElementComponentData<string | number>,
		nextData: UpdateProps
	) => void,
	data: ElementComponentData<string | number>
) => {
	// Pending value is one that is currently being sync'd to the API
	// We use it as our "currentValue" if we need to prep another update
	// before the previous value is finished sync'ing to the API
	const previousData = useRef(data);

	const update = useRef(
		_.debounce((next: UpdateProps) => {
			callback({ ...previousData.current }, next);
			previousData.current = next;
		}, 1000)
	);

	// _value is our local value
	const [_data, setData] = useState(data);

	const onChange = useCallback(
		(value: string | number) => {
			const next = {
				..._data,
				value,
			};
			setData(next);
			update.current(next);
		},
		[_data]
	);

	const flushChanges = useCallback(() => {
		update.current.flush();
	}, []);

	useEffect(() => {
		const timer = update;
		const prev = previousData;

		// If our data is updated externally
		if (data.value !== prev.current.value) {
			// If we have no local change update our local data
			if (_data.value === prev.current.value) setData(data);

			// Update our prevData
			prev.current = data;
		}

		// Flush any changes before
		// we unmount
		return () => {
			timer.current.flush();
		};
		// disabled - false warning - this is the behaviour we require
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	return {
		value: _data.value ?? "",
		onChange,
		onBlur: flushChanges,
		flushChanges,
	};
};

const useDebounceSetDataOnApply = (
	callback: (
		currentData: ElementComponentData<string | number>,
		nextData: UpdateProps
	) => void,
	data: ElementComponentData<string | number>,
	isApplyClicked: boolean,
	isFieldEdited: boolean,
	type?: "number" | "text"
) => {
	// Pending value is one that is currently being sync'd to the API
	// We use it as our "currentValue" if we need to prep another update
	// before the previous value is finished sync'ing to the API
	const previousData = useRef(data);

	const update = useRef(
		_.debounce((next: UpdateProps) => {
			callback({ ...previousData.current }, next);
			previousData.current = next;
		}, 1000)
	);

	// _value is our local value
	const [_data, setData] = useState(() => {
		data.value =
			type === "number" ? parseFloat(data.value as string) : data.value;
		return data;
	});

	const onChange = useCallback(
		(value: string | number) => {
			const next = {
				..._data,
				value,
			};
			setData(next);
		},
		[_data]
	);

	useEffect(() => {
		if (isApplyClicked && isFieldEdited) {
			update.current(_data);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isApplyClicked]);

	const flushChanges = useCallback(() => {
		update.current.flush();
	}, []);

	useEffect(() => {
		const timer = update;
		const prev = previousData;

		// If our data is updated externally
		if (data.value !== prev.current.value) {
			// If we have no local change update our local data
			if (_data.value === prev.current.value) {
				data.value =
					type === "number"
						? parseFloat(data.value as string)
						: data.value;
				setData(data);
			}
			// Update our prevData
			prev.current = data;
		}

		// Flush any changes before
		// we unmount
		return () => {
			timer.current.flush();
		};
		// disabled - false warning - this is the behaviour we require
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);
	return {
		value: _data.value ?? "",
		onChange,
		onBlur: flushChanges,
		flushChanges,
	};
};

export { useDebounceSetDataReactive, useDebounceSetDataOnApply };
